import { useCallback, useEffect, useRef, useState } from "react"
import {
  Animated,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { useIsFocused } from "@react-navigation/native"
import { useEvent } from "expo"
import * as NavigationBar from "expo-navigation-bar"
import { useFocusEffect, useLocalSearchParams } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useVideoPlayer, VideoView } from "expo-video"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { SidekickView } from "src/components/SidekickView"
import { useMediaContext } from "src/context/MediaContextProvider"
import { useMicrophone } from "src/context/MicrophoneContext"
import { useVisemes } from "src/context/VisemeContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useStatusBarCompensation } from "src/hooks/useStatusBarCompensation"
import { useWebMouseActivity } from "src/hooks/useWebMouseActivity"
import { backToRoot } from "src/utils/routerHelpers"
import { formatSecondsToTimestamp } from "src/utils/time"

export default function AudioPlayerScreen() {
  const { audioUrl, audioTitle, startTime, autoplay, resourceId, taskId, totalLength } =
    useLocalSearchParams<{
      audioUrl?: string | string[]
      audioTitle?: string | string[]
      startTime?: string
      autoplay?: string
      resourceId?: string
      taskId?: string
      totalLength?: string
    }>()
  const resolvedAudioUrl = Array.isArray(audioUrl) ? audioUrl[0] : audioUrl
  const resolvedAudioTitle = Array.isArray(audioTitle) ? audioTitle[0] : audioTitle || "Audio File"
  const resolvedStartTime = startTime ? parseFloat(startTime) : 0
  const _resolvedAutoplay = autoplay === "true" || autoplay === "1"
  const { theme, navTheme } = useAppTheme()
  const { colors } = theme
  const insets = useSafeAreaInsets()
  const { effectiveTopInset } = useStatusBarCompensation()
  const { width, height } = useWindowDimensions()
  const isLandscape = width > height
  // True only while this screen is the focused/top route. Gates autoplay so a
  // hidden/stacked audio player instance never starts on its own.
  const isFocused = useIsFocused()

  const [position, setPosition] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isScrubbing, setIsScrubbing] = useState(false)
  const [, setScrubTime] = useState<number | null>(null)
  const pendingSeekRef = useRef<number | null>(null)

  const source = resolvedAudioUrl && resolvedAudioUrl.length > 0 ? resolvedAudioUrl : ""

  // Use video player for audio (expo-video supports audio files)
  const player = useVideoPlayer(source, (p) => {
    p.loop = false
    p.timeUpdateEventInterval = 0.1
  })

  const { isListening } = useMicrophone()
  const { stopSpeech, isSpeaking } = useVisemes()
  const { setMediaContext, updateOffset, updateTotalLength, clearMediaContext } = useMediaContext()

  const hasAutoplayed = useRef(false)
  const controlsOpacity = useRef(new Animated.Value(1)).current
  const overlayOpacity = useRef(new Animated.Value(0)).current
  const [isOverlayVisible, setIsOverlayVisible] = useState(false)
  const [overlayIcon, setOverlayIcon] = useState<"play-circle-filled" | "pause-circle">(
    "play-circle-filled",
  )
  const [isClosing, setIsClosing] = useState(false)
  const currentAnimationToken = useRef<object | null>(null)

  const { isPlaying } = useEvent(player, "playingChange", { isPlaying: player.playing })

  const { isMouseActive, handleMouseMove } = useWebMouseActivity()
  const [isTyping, setIsTyping] = useState(false)
  // On web: show controls while mouse/keyboard active, user is typing, or paused.
  // isTyping prevents controls from fading while the text input is open, which
  // would otherwise trigger SidekickView's returnToIdle and collapse the input.
  // On native: show only when paused (unchanged).
  const shouldShowControls = !isPlaying || (Platform.OS === "web" && (isMouseActive || isTyping))

  // Unlock orientation for audio player
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.unlockAsync().catch(() => {})
      // Don't re-lock on cleanup to prevent jitter when auto-navigating between media
    }, []),
  )

  // Register media context for context-aware chat responses
  useEffect(() => {
    if (resourceId) {
      setMediaContext({
        resourceId,
        totalLength,
        taskId,
        title: resolvedAudioTitle,
        initialOffset: formatSecondsToTimestamp(resolvedStartTime),
      })
    }
    return () => {
      clearMediaContext()
    }
  }, [
    resourceId,
    totalLength,
    taskId,
    resolvedAudioTitle,
    resolvedStartTime,
    setMediaContext,
    clearMediaContext,
  ])

  // Update media context offset during playback
  useEffect(() => {
    if (resourceId && !isScrubbing) {
      updateOffset(formatSecondsToTimestamp(position))
    }
  }, [resourceId, position, isScrubbing, updateOffset])

  // Update media context total length when audio duration is loaded
  useEffect(() => {
    if (resourceId && duration > 0) {
      const formattedDuration = formatSecondsToTimestamp(duration)
      updateTotalLength(formattedDuration)
    }
  }, [resourceId, duration, updateTotalLength])

  // Handle Android navigation bar
  useEffect(() => {
    if (Platform.OS === "android") {
      NavigationBar.setVisibilityAsync("hidden")
      NavigationBar.setBehaviorAsync("overlay-swipe")
      NavigationBar.setBackgroundColorAsync("#000000")
      NavigationBar.setButtonStyleAsync("light")
    }

    return () => {
      if (Platform.OS === "android") {
        NavigationBar.setVisibilityAsync("visible")
        NavigationBar.setBackgroundColorAsync(theme.colors.background)
        NavigationBar.setButtonStyleAsync(navTheme.dark ? "light" : "dark")
      }
    }
  }, [theme.colors.background, navTheme.dark])

  // Stop speech when audio starts playing
  useEffect(() => {
    if (isPlaying) {
      stopSpeech()
      // Don't hide avatar for audio - no visual content competing for space
    }
  }, [isPlaying, stopSpeech])

  // Animate controls opacity: fade out when hidden, fade in when visible.
  // On web this reacts to both playback state and mouse activity.
  useEffect(() => {
    Animated.timing(controlsOpacity, {
      toValue: shouldShowControls ? 1 : 0,
      duration: 300,
      useNativeDriver: true,
    }).start()
  }, [shouldShowControls, controlsOpacity])

  // Pause audio when listening (Talk mode) or when the sidekick starts speaking
  // so its TTS reply is audible. On Android, the playing track holds exclusive
  // audio focus (DoNotMix), so we must pause before the sidekick can be heard.
  useEffect(() => {
    if (isListening || isSpeaking) {
      player.pause()
    }
  }, [isListening, isSpeaking, player])

  const { status: playerStatus } = useEvent(player, "statusChange", { status: player.status })
  const { duration: loadedDuration } = useEvent(player, "sourceLoad", {
    videoSource: null,
    duration: player.duration ?? 0,
    availableVideoTracks: [],
    availableSubtitleTracks: [],
    availableAudioTracks: [],
  })

  const { currentTime } = useEvent(player, "timeUpdate", {
    currentTime: player.currentTime ?? 0,
    currentLiveTimestamp: null,
    currentOffsetFromLive: null,
    bufferedPosition: player.bufferedPosition ?? 0,
  })

  const syncDurationFromPlayer = useCallback(() => {
    const nativeDuration = player.duration ?? 0
    if (nativeDuration > 0 && Number.isFinite(nativeDuration)) {
      setDuration((current) => {
        return Math.abs(current - nativeDuration) > 0.1 ? nativeDuration : current
      })
    }
  }, [player])

  useEffect(() => {
    if (loadedDuration && Number.isFinite(loadedDuration) && loadedDuration > 0) {
      setDuration((current) => {
        return Math.abs(current - loadedDuration) > 0.1 ? loadedDuration : current
      })
      return
    }

    if (playerStatus === "readyToPlay") {
      syncDurationFromPlayer()
    }
  }, [loadedDuration, playerStatus, syncDurationFromPlayer])

  // Sync duration from player when we have time updates but duration is still 0 (needed for web/Android where sourceLoad may not provide duration)
  useEffect(() => {
    if (duration > 0) {
      return
    }

    syncDurationFromPlayer()
  }, [currentTime, duration, syncDurationFromPlayer])

  useEffect(() => {
    if (!isScrubbing && typeof currentTime === "number" && !Number.isNaN(currentTime)) {
      // If we have a pending seek, check if the player has caught up to it
      if (pendingSeekRef.current !== null) {
        // Consider the seek complete if the player is within 0.5s of the target
        if (Math.abs(currentTime - pendingSeekRef.current) < 0.5) {
          pendingSeekRef.current = null
        }
      }
      // Only update position from player if we don't have a pending seek
      if (pendingSeekRef.current === null) {
        setPosition(currentTime)
      }
    }
  }, [currentTime, isScrubbing])

  // Autoplay once the player is ready and the sidekick has finished speaking.
  // Do not autoplay while the user is listening (Talk) to avoid the mic capturing playback.
  // Seek here (not in the useVideoPlayer setup callback) because the setup runs before the
  // source is loaded — the native player resets currentTime to 0 once loading completes.
  useEffect(() => {
    if (playerStatus !== "readyToPlay" || isSpeaking || isListening || hasAutoplayed.current) return
    // Don't autoplay unless this screen is actually on top. If several media
    // events arrived together (or another player was pushed over this one),
    // this instance can be mounted but hidden — autoplaying here would start
    // audio for a player the user can't see.
    if (!isFocused) return
    hasAutoplayed.current = true
    if (resolvedStartTime > 0) {
      pendingSeekRef.current = resolvedStartTime
      player.currentTime = resolvedStartTime
    }
    player.play()
  }, [playerStatus, isSpeaking, isListening, isFocused, player, resolvedStartTime])

  // Belt-and-suspenders: guarantee playback stops if this screen unmounts. On
  // iOS expo-video can keep emitting audio briefly if the player isn't paused
  // before it's released. The cleanup only runs on unmount / player swap, so it
  // never fights autoplay.
  useEffect(() => {
    return () => {
      try {
        player.pause()
      } catch {
        // Player may already be released; ignore.
      }
    }
  }, [player])

  const handleContentPress = useCallback(() => {
    // Toggle play/pause on tap
    const willPlay = !isPlaying
    if (isPlaying) {
      player.pause()
    } else {
      player.play()
    }
    // Create unique token for this animation FIRST to invalidate any in-progress animation
    const animationToken = {}
    currentAnimationToken.current = animationToken
    // Stop any in-progress animation cleanly
    overlayOpacity.stopAnimation(() => {
      // After animation stops, update icon and restart
      setOverlayIcon(willPlay ? "play-circle-filled" : "pause-circle")
      overlayOpacity.setValue(0.75)
      setIsOverlayVisible(true)
      // Start new animation
      Animated.timing(overlayOpacity, {
        toValue: 0,
        duration: 800,
        delay: 1500,
        useNativeDriver: true,
      }).start(({ finished }) => {
        // Only hide if this is still the current animation
        if (finished && currentAnimationToken.current === animationToken) {
          setIsOverlayVisible(false)
        }
      })
    })
  }, [isPlaying, player, overlayOpacity])

  const togglePause = useCallback(() => {
    const willPlay = !isPlaying
    if (isPlaying) {
      player.pause()
    } else {
      player.play()
    }
    // Create unique token for this animation FIRST to invalidate any in-progress animation
    const animationToken = {}
    currentAnimationToken.current = animationToken
    // Stop any in-progress animation cleanly
    overlayOpacity.stopAnimation(() => {
      // After animation stops, update icon and restart
      setOverlayIcon(willPlay ? "play-circle-filled" : "pause-circle")
      overlayOpacity.setValue(0.75)
      setIsOverlayVisible(true)
      // Start new animation
      Animated.timing(overlayOpacity, {
        toValue: 0,
        duration: 800,
        delay: 1500,
        useNativeDriver: true,
      }).start(({ finished }) => {
        // Only hide if this is still the current animation
        if (finished && currentAnimationToken.current === animationToken) {
          setIsOverlayVisible(false)
        }
      })
    })
  }, [isPlaying, player, overlayOpacity])

  // Handle mode changes from SidekickView
  const handleModeChange = useCallback((mode: "idle" | "talk" | "type") => {
    setIsTyping(mode === "type")
  }, [])

  if (!resolvedAudioUrl) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Unable to load audio.</Text>
      </View>
    )
  }

  return (
    <View
      style={[styles.container, { backgroundColor: colors.background }]}
      {...({ onMouseMove: handleMouseMove } as any)}
    >
      <StatusBar style="auto" hidden={isPlaying && isLandscape} />

      {/* Close button - above SidekickView */}
      <Animated.View
        style={[
          styles.closeButton,
          {
            top: effectiveTopInset + 22,
            right: insets.right + 12,
          },
          Platform.OS === "web" && ({ position: "fixed" } as any),
        ]}
        pointerEvents="auto"
      >
        <TouchableOpacity
          onPress={async () => {
            // Unmount AvatarOverlay if in landscape to prevent crash during orientation change (APP-738)
            if (isLandscape) {
              setIsClosing(true)
              // Wait for React to unmount the component
              await new Promise((resolve) => setTimeout(resolve, 0))
            }
            backToRoot()
          }}
          style={[styles.closeButtonInner, { backgroundColor: colors.buttonBackground }]}
          accessibilityRole="button"
          accessibilityLabel="Close audio player"
        >
          <MaterialIcons name="close" size={20} color={colors.buttonText} />
        </TouchableOpacity>
      </Animated.View>

      {!isClosing && (
        <SidekickView
          showAvatar
          autoShowAvatarOnSpeech
          showCaptions
          showCCToggle
          showButtonCluster
          compactButtonCluster
          darkBackground
          showControlsBarBackground={false}
          controlsOpacity={controlsOpacity}
          controlsVisible={shouldShowControls}
          onContentPress={handleContentPress}
          onModeChange={handleModeChange}
          riveOwner="video"
          controlsConfig={{
            type: "playback",
            isPlaying,
            onTogglePlay: togglePause,
            position,
            duration,
            isScrubbing,
            onScrubStart: () => {
              setIsScrubbing(true)
              setScrubTime(position)
            },
            onScrubChange: (value: number) => {
              setPosition(value)
              setScrubTime(value)
            },
            onScrubEnd: (value: number) => {
              setIsScrubbing(false)
              setScrubTime(null)
              // Keep the position at the scrubbed value to prevent jumping
              setPosition(value)
              pendingSeekRef.current = value
              player.currentTime = value
            },
          }}
        >
          {/* Hidden VideoView so expo-video has an HTML media element to play (required on web) */}
          <VideoView
            player={player}
            style={styles.hiddenVideoView}
            nativeControls={false}
            contentFit="contain"
            allowsFullscreen={false}
            allowsPictureInPicture={false}
            pointerEvents="none"
            {...(Platform.OS === "web" && { crossOrigin: "anonymous" as const })}
          />
          {/* Audio visualization */}
          <View style={styles.audioContainer}>
            <MaterialIcons name="volume-up" size={120} color={colors.text} />
            <Text style={[styles.audioTitle, { color: colors.text }]}>{resolvedAudioTitle}</Text>
          </View>
        </SidekickView>
      )}

      {/* Play/Pause overlay animation */}
      {isOverlayVisible && (
        <Animated.View
          pointerEvents="none"
          style={[styles.fullscreenOverlay, { opacity: overlayOpacity }]}
        >
          <MaterialIcons name={overlayIcon} size={120} color="#FFFFFF" accessibilityRole="image" />
        </Animated.View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  errorContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "#000000",
  },
  errorText: {
    color: "#FFFFFF",
    textAlign: "center",
  },
  hiddenVideoView: {
    position: "absolute",
    width: 1,
    height: 1,
    opacity: 0,
    overflow: "hidden",
  },
  audioContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 32,
  },
  audioTitle: {
    fontSize: 24,
    fontWeight: "600",
    textAlign: "center",
    marginTop: 32,
    fontFamily: "figtreeMedium",
  },
  closeButton: {
    position: "absolute",
    zIndex: 3200,
  },
  closeButtonInner: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
  },
  fullscreenOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.3)",
    zIndex: 2050,
  },
})
